import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  Subject,
  catchError,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { FolderItem } from 'src/app/api/services/folder';
import { ListItemKind } from 'src/app/components/scrolling-list';
import {
  FolderService,
  FolderTreeItem,
} from 'src/app/shared/services/folder.service';

export type FolderBrowserKind = Extract<
  ListItemKind,
  'Display' | 'Schedule' | 'Presentation'
>;

/**
 * Browses one folder level at a time, for lists that show folders alongside
 * their items. It owns no selection or move semantics: it only answers which
 * folders belong to the level currently open, plus the tree the breadcrumb
 * needs.
 *
 * Provide it on the component that browses, so each dialog or page keeps its
 * own position in the hierarchy.
 */
@Injectable()
export class FolderBrowserService {
  private readonly folderService = inject(FolderService);

  private readonly openRequests$ = new Subject<string>();
  private readonly folders$ = new BehaviorSubject<FolderItem[]>([]);
  private readonly folderTree$ = new BehaviorSubject<FolderTreeItem[]>([]);
  private readonly currentFolderId$ = new BehaviorSubject<string>('');
  private readonly loading$ = new BehaviorSubject<boolean>(false);

  private kind: FolderBrowserKind;

  readonly folders = toSignal(this.folders$, { requireSync: true });
  readonly folderTree = toSignal(this.folderTree$, { requireSync: true });
  readonly currentFolderId = toSignal(this.currentFolderId$, {
    requireSync: true,
  });
  readonly loading = toSignal(this.loading$, { requireSync: true });

  constructor() {
    this.openRequests$
      .pipe(
        tap(() => this.loading$.next(true)),
        // Only the newest request may publish. Browsing quickly through
        // folders would otherwise let a slower earlier response overwrite the
        // level the user is actually looking at.
        switchMap((folderId) =>
          from(this.loadLevel(folderId)).pipe(
            map((folders) => ({ folderId, folders })),
            catchError((error) => {
              console.error(error);
              return of({ folderId, folders: [] as FolderItem[] });
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ folderId, folders }) => {
        this.currentFolderId$.next(folderId);
        this.folders$.next(folders);
        this.loading$.next(false);
      });
  }

  /**
   * Starts browsing `kind` at the root folder and loads the tree behind the
   * breadcrumb.
   */
  init(kind: FolderBrowserKind) {
    this.kind = kind;
    this.loadTree();
    this.open('');
  }

  /** Opens a folder, or the root folder when no id is given. */
  open(folderId?: string) {
    this.openRequests$.next(folderId ?? '');
  }

  /** Reloads the level currently open. */
  reload() {
    this.open(this.currentFolderId$.value);
  }

  private loadLevel(folderId: string) {
    // `folderId` scopes the query by parent — an empty one still means the
    // root folder, not "every level" — and `loadFolders` follows the cursor
    // until no page is left.
    return this.folderService.loadFolders(this.kind, {
      ...this.baseSearch(),
      folderId,
    });
  }

  private async loadTree() {
    try {
      // An unscoped load is what builds `FolderService.folderTree`.
      await this.folderService.loadFolders(this.kind, this.baseSearch());
      this.folderTree$.next(this.folderService.folderTree);
    } catch (error) {
      console.error(error);
    }
  }

  private baseSearch() {
    return {
      query: '',
      includeSubcompanies: false,
      sortBy: 'name',
      reverse: false,
    };
  }
}
